import { useState, useMemo } from "react"
import { Button } from "@components/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { Checkbox } from "@components/common/checkbox"
import { cn } from "@pma/utils/helpers"
import { ChevronDown, X, SlidersHorizontal } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import { useAuth } from "@pma/stores/auth-store"
import { AnalyticsFilters } from "@pma/types"

// ---------------------------------------------------------------------------
// Shared Types
// ---------------------------------------------------------------------------

export const EMPTY_FILTERS: AnalyticsFilters = {
  projectIds: [],
  dateRange: { from: null, to: null },
  userIds: [],
}

// ---------------------------------------------------------------------------
// Date Preset Type
// ---------------------------------------------------------------------------

type DatePreset = "all" | "today" | "week" | "month" | "quarter"

function getDatePresetRange(preset: DatePreset): { from: Date | null; to: Date | null } {
  if (preset === "all") return { from: null, to: null }
  const now = new Date()
  const to = new Date()
  to.setHours(23, 59, 59, 999) // M1: end-of-day so entire day is captured
  const from = new Date()
  switch (preset) {
    case "today":
      from.setHours(0, 0, 0, 0)
      break
    case "week":
      from.setDate(now.getDate() - 7)
      break
    case "month":
      from.setMonth(now.getMonth() - 1)
      break
    case "quarter":
      from.setMonth(now.getMonth() - 3)
      break
  }
  return { from, to }
}

// ---------------------------------------------------------------------------
// Multi-Select Dropdown
// ---------------------------------------------------------------------------

interface MultiSelectProps {
  label: string
  allLabel: string
  options: { id: number; name: string }[]
  selectedIds: number[]
  onSelectionChange: (ids: number[]) => void
  isHebrew: boolean
}

function MultiSelectDropdown({
  label,
  allLabel,
  options,
  selectedIds,
  onSelectionChange,
  isHebrew,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options
    const lower = search.toLowerCase()
    return options.filter((o) => o.name.toLowerCase().includes(lower))
  }, [options, search])

  const displayText =
    selectedIds.length === 0
      ? allLabel
      : selectedIds.length === 1
        ? options.find((o) => o.id === selectedIds[0])?.name || allLabel
        : `${selectedIds.length} ${isHebrew ? "נבחרו" : "selected"}`

  const toggleId = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((sid) => sid !== id))
    } else {
      onSelectionChange([...selectedIds, id])
    }
  }

  return (
    <div className='flex shrink-0 items-center gap-1.5'>
      <span className='whitespace-nowrap text-xs font-medium text-muted-foreground'>{label}:</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            size='sm'
            className='h-7 min-w-[100px] max-w-[160px] justify-between gap-1 px-2 text-xs font-normal'
          >
            <span className='truncate'>{displayText}</span>
            <ChevronDown
              className={cn(
                "h-3 w-3 shrink-0 opacity-50 transition-transform duration-150",
                open && "rotate-180",
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-56 p-0'
          align={isHebrew ? "end" : "start"}
          dir={isHebrew ? "rtl" : "ltr"}
        >
          <div className='border-b p-2'>
            <input
              type='text'
              placeholder={isHebrew ? "חיפוש..." : "Search..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='w-full rounded border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring'
            />
          </div>
          <div className='max-h-48 overflow-y-auto p-1'>
            {/* Select All */}
            <button
              className='flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent'
              onClick={() => onSelectionChange([])}
            >
              <Checkbox checked={selectedIds.length === 0} />
              <span className='font-medium'>{allLabel}</span>
            </button>
            {filteredOptions.map((opt) => (
              <button
                key={opt.id}
                className='flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent'
                onClick={() => toggleId(opt.id)}
              >
                <Checkbox checked={selectedIds.includes(opt.id)} />
                <span className='truncate'>{opt.name}</span>
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <p className='p-2 text-center text-xs text-muted-foreground'>
                {isHebrew ? "לא נמצאו תוצאות" : "No results"}
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Removable Chip
// ---------------------------------------------------------------------------

interface ChipProps {
  label: string
  onRemove: () => void
}

function Chip({ label, onRemove }: ChipProps) {
  const { translate } = useTranslator()
  return (
    <span className='inline-flex select-none items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary'>
      {label}
      <button
        onClick={onRemove}
        aria-label={translate("Remove filter")}
        className='flex h-3.5 w-3.5 items-center justify-center rounded-full transition-colors hover:bg-primary/20'
      >
        <X className='h-2.5 w-2.5' />
      </button>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Main Filter Bar
// ---------------------------------------------------------------------------

interface AnalyticsFilterBarProps {
  filters: AnalyticsFilters
  onFiltersChange: (filters: AnalyticsFilters) => void
  /** Project options passed from parent (shared hook) — eliminates duplicate fetch */
  projectOptions?: { id: number; name: string }[]
}

export function AnalyticsFilterBar({
  filters,
  onFiltersChange,
  projectOptions: externalProjectOptions,
}: AnalyticsFilterBarProps) {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"
  const organizationUsers = useAuth((s) => s.authConfig?.organizationUsers)
  // Track active date preset as state (M2: derive never matched after selection)
  const [activeDatePreset, setActiveDatePreset] = useState<DatePreset>("all")

  const projectOptions = externalProjectOptions || []

  // User options from auth — selector narrows to organizationUsers only,
  // so unrelated authConfig changes (setTabManager, etc.) won't re-render this component.
  const userOptions = useMemo(() => {
    const users = organizationUsers || []
    return users
      .filter((u: any) => u.name)
      .map((u: any) => ({
        id: u.id,
        name: u.name,
      }))
  }, [organizationUsers])

  const datePresets: { key: DatePreset; label: string }[] = [
    { key: "all", label: isHebrew ? "הכל" : "All" },
    { key: "today", label: isHebrew ? "היום" : "Today" },
    { key: "week", label: isHebrew ? "שבוע" : "Week" },
    { key: "month", label: isHebrew ? "חודש" : "Month" },
    { key: "quarter", label: isHebrew ? "רבעון" : "Quarter" },
  ]

  const hasActiveFilters =
    filters.projectIds.length > 0 || filters.userIds.length > 0 || filters.dateRange.from !== null

  const clearAll = () => onFiltersChange(EMPTY_FILTERS)

  // Names for chips
  const selectedProjectChips = useMemo(
    () =>
      filters.projectIds
        .map((id) => ({ id, name: projectOptions.find((p: any) => p.id === id)?.name }))
        .filter((c) => Boolean(c.name)) as { id: number; name: string }[],
    [filters.projectIds, projectOptions],
  )

  const selectedUserChips = useMemo(
    () =>
      filters.userIds
        .map((id) => ({ id, name: userOptions.find((u: any) => u.id === id)?.name }))
        .filter((c) => Boolean(c.name)) as { id: number; name: string }[],
    [filters.userIds, userOptions],
  )

  const showChipsRow = selectedProjectChips.length > 0 || selectedUserChips.length > 0

  return (
    <div
      dir={isHebrew ? "rtl" : "ltr"}
      className='space-y-2 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2'
    >
      {/* ── Filter Controls ── single row on desktop, wraps on mobile ── */}
      <div className='flex flex-wrap items-center gap-2 sm:flex-nowrap sm:gap-3'>
        {/* Icon */}
        <SlidersHorizontal className='h-3.5 w-3.5 shrink-0 text-muted-foreground' />

        {/* Project Selector */}
        <MultiSelectDropdown
          label={isHebrew ? "פרויקט" : "Project"}
          allLabel={isHebrew ? "כל הפרויקטים" : "All Projects"}
          options={projectOptions}
          selectedIds={filters.projectIds}
          onSelectionChange={(ids) => onFiltersChange({ ...filters, projectIds: ids })}
          isHebrew={isHebrew}
        />

        {/* Date Range Presets */}
        <div className='flex shrink-0 items-center gap-1.5'>
          <span className='whitespace-nowrap text-xs font-medium text-muted-foreground'>
            {isHebrew ? "תקופה" : "Period"}:
          </span>
          <div className='flex items-center gap-0.5 rounded-md border bg-background p-0.5'>
            {datePresets.map((preset) => {
              const isActive = activeDatePreset === preset.key
              return (
                <button
                  key={preset.key}
                  onClick={() => {
                    const range = getDatePresetRange(preset.key)
                    setActiveDatePreset(preset.key)
                    onFiltersChange({ ...filters, dateRange: range })
                  }}
                  className={cn(
                    "h-6 rounded-sm px-2 text-xs font-medium transition-all duration-150",
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  {preset.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* User Selector */}
        {userOptions.length > 0 && (
          <MultiSelectDropdown
            label={isHebrew ? "משתמש" : "User"}
            allLabel={isHebrew ? "כולם" : "All Users"}
            options={userOptions}
            selectedIds={filters.userIds}
            onSelectionChange={(ids) => onFiltersChange({ ...filters, userIds: ids })}
            isHebrew={isHebrew}
          />
        )}

        {/* Spacer pushes "Clear All" to the far end */}
        <div className='flex-1' />

        {/* Clear All — subtle text link, only when filters active */}
        {hasActiveFilters && (
          <button
            onClick={clearAll}
            className='shrink-0 whitespace-nowrap text-xs text-muted-foreground transition-colors hover:text-destructive'
          >
            {isHebrew ? "נקה הכל" : "Clear all"}
          </button>
        )}
      </div>

      {/* ── Active-filter chips ── only shown when something is selected ── */}
      {showChipsRow && (
        <div className='flex flex-wrap gap-1.5 pt-0.5'>
          {selectedProjectChips.map((chip) => (
            <Chip
              key={`p-${chip.id}`}
              label={chip.name}
              onRemove={() =>
                onFiltersChange({
                  ...filters,
                  projectIds: filters.projectIds.filter((pid) => pid !== chip.id),
                })
              }
            />
          ))}
          {selectedUserChips.map((chip) => (
            <Chip
              key={`u-${chip.id}`}
              label={chip.name}
              onRemove={() =>
                onFiltersChange({
                  ...filters,
                  userIds: filters.userIds.filter((uid) => uid !== chip.id),
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default AnalyticsFilterBar
